/**
 * Preview Questions Landing Component
 * Provides two options: CSV upload or manual ID entry
 */

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { useToast } from "../../contexts/ToastContext";
import { parseQuestionIdsFromCSV, formatFileSize, CSVParseError, CSV_ERROR_CODES } from "../../helpers/csvParser";
import { globalConfig } from "../../config/globalConfig";
import importIcon from "../../assets/images/file-import.svg";
import "./PreviewQuestionsLanding.scss";

const MAX_QUESTION_IDS = globalConfig.maxQuestionIds;

const PreviewQuestionsLanding = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [questionIds, setQuestionIds] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { getResourceValue } = useResources();
  const { showError } = useToast();

  // Get translated error message based on error code
  const getErrorMessage = (code: string, fallback: string): { title: string; message: string } => {
    const errorMessages: Record<string, { titleKey: string; titleDefault: string; messageKey: string; messageDefault: string }> = {
      [CSV_ERROR_CODES.INVALID_FILE_FORMAT]: {
        titleKey: "invalid_file_format",
        titleDefault: "Invalid File Format",
        messageKey: "csv_error_invalid_format",
        messageDefault: "Please upload a CSV file.",
      },
      [CSV_ERROR_CODES.MISSING_QUESTION_ID_COLUMN]: {
        titleKey: "missing_column",
        titleDefault: "Missing Column",
        messageKey: "csv_error_missing_column",
        messageDefault: "CSV file must contain a 'question_id' column.",
      },
      [CSV_ERROR_CODES.NO_VALID_QUESTION_IDS]: {
        titleKey: "no_questions_found",
        titleDefault: "No Questions Found",
        messageKey: "csv_error_no_valid_ids",
        messageDefault: "No valid question IDs found in the CSV file.",
      },
      [CSV_ERROR_CODES.INVALID_QUESTION_IDS]: {
        titleKey: "invalid_question_ids",
        titleDefault: "Invalid Question IDs",
        messageKey: "csv_error_invalid_ids",
        messageDefault: "Invalid question IDs found (must be 12 digits).",
      },
      [CSV_ERROR_CODES.PROCESSING_ERROR]: {
        titleKey: "processing_error",
        titleDefault: "Processing Error",
        messageKey: "csv_error_processing",
        messageDefault: "Error processing CSV file.",
      },
      [CSV_ERROR_CODES.PARSING_ERROR]: {
        titleKey: "parsing_error",
        titleDefault: "Parsing Error",
        messageKey: "csv_error_parsing",
        messageDefault: "Error parsing CSV file.",
      },
    };

    const errorConfig = errorMessages[code];
    if (errorConfig) {
      return {
        title: getResourceValue(errorConfig.titleKey, errorConfig.titleDefault),
        message: getResourceValue(errorConfig.messageKey, errorConfig.messageDefault),
      };
    }

    return {
      title: getResourceValue("error", "Error"),
      message: fallback,
    };
  };

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith(".csv")) {
      showError(
        getResourceValue("invalid_file_format", "Invalid File Format"),
        getResourceValue("please_select_csv", "Please select a CSV file")
      );
      return;
    }

    setIsProcessing(true);

    try {
      const ids = await parseQuestionIdsFromCSV(file);
      if (ids.length > MAX_QUESTION_IDS) {
        showError(
          getResourceValue("error", "Error"),
          getResourceValue(
            "csv_error_max_questions_exceeded",
            `Maximum ${MAX_QUESTION_IDS} question IDs allowed. Your file contains ${ids.length} question IDs.`
          )
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      setQuestionIds(ids);
    } catch (err) {
      // Reset file selection
      setSelectedFile(null);
      setQuestionIds([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Show error toast with translated message
      if (err instanceof CSVParseError) {
        const { title, message } = getErrorMessage(err.code, err.message);
        showError(title, message);
      } else {
        showError(
          getResourceValue("error", "Error"),
          err instanceof Error ? err.message : getResourceValue("failed_parse_csv", "Failed to parse CSV file")
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    setQuestionIds([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreviewQuestions = () => {
    if (questionIds.length > 0) {
      navigate("/preview-questions/results", {
        state: {
          questionIds,
          source: "csv",
        },
      });
    }
  };

  const handleEnterManually = () => {
    navigate("/questions/lookup");
  };

  return (
    <div className="preview-questions-landing">
      <div className="preview-questions-landing__content">
        <h2 className="preview-questions-landing__title">
          {getResourceValue("preview_questions", "Preview Questions")}
        </h2>

        {/* CSV Upload Section - Always visible */}
        <div
          className={`upload-area ${isDragging ? "upload-area--dragging" : ""} ${!selectedFile ? "upload-area--clickable" : ""}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={!selectedFile && !isProcessing ? handleBrowseClick : undefined}
          role={!selectedFile ? "button" : undefined}
          tabIndex={!selectedFile ? 0 : undefined}
          onKeyDown={!selectedFile ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBrowseClick();
            }
          } : undefined}
        >
          <div className="upload-area__icon">
            <img src={importIcon} alt="Import" />
          </div>
          <p className="upload-area__title">
            {getResourceValue("import_csv_file", "Import your csv file")}
          </p>
          <p className="upload-area__subtitle">
            {getResourceValue("supported_file_format_csv", "Supported file format: .CSV")}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
          />
        </div>

        {/* Imported Files Section - Only show when file is selected */}
        {selectedFile && (
          <div className="uploaded-file">
            <div className="uploaded-file__header">
              <h3>{getResourceValue("imported_files", "Imported Files")}</h3>
            </div>
            <div className="uploaded-file__item">
              <div className="uploaded-file__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                    fill="#8BC34A"
                    stroke="#8BC34A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 2V8H20"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="uploaded-file__info">
                <p className="uploaded-file__name">{selectedFile.name}</p>
                <p className="uploaded-file__meta">
                  CSV • {formatFileSize(selectedFile.size)}
                </p>
              </div>
              <button
                className="uploaded-file__cancel"
                onClick={handleCancelFile}
                aria-label="Cancel"
              >
                <i className="f-icon cancel-icon"></i>{" "}
                {getResourceValue("cancel", "Cancel")}
              </button>
            </div>
            <div className="uploaded-file__actions">
              <button
                className="btn btn-green preview-btn"
                onClick={handlePreviewQuestions}
                disabled={questionIds.length === 0}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {getResourceValue("preview_questions", "Preview Questions")}
              </button>
            </div>
          </div>
        )}

        {/* Divider with "Or" - Only show when no file is selected */}
        {!selectedFile && (
          <>
            <div className="divider">
              <span>{getResourceValue("or", "Or")}</span>
            </div>

            {/* Manual Entry Button */}
            <button
              className="btn btn-green manual-entry-btn"
              onClick={handleEnterManually}
            >
              {getResourceValue("enter_ids_manually", "Enter IDs Manually")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PreviewQuestionsLanding;
